/** @jsxImportSource @emotion/react */
"use client";
import type { Interpolation } from "@emotion/react";

import type { ThemeType } from "../../../../../global/emotion";
import {
  colors,
  constantSemanticColors,
  radius,
  spacing,
  stroke,
} from "../../../../../global/emotion";
import type { HallowElement } from "../../../../types/hallowElement.types";
import type { HallowElementProps } from "../../../../types/hallowElementProps.types";
import type { WithAsChild } from "../../../../types/withAsChild.types";
import { determineElementFromAsChild } from "../../../../utils/determineElementFromAsChild";
import type { TextProps } from "../Text/Text.emotion";
import { Text } from "../Text/Text.emotion";

const Element: HallowElement = "div";

export type TagProps = WithAsChild<HallowElementProps<typeof Element>> & {
  textProps?: Partial<TextProps>;
  variant?:
    | "secondary"
    | "accent"
    | "neutral"
    | "lighten"
    | "outline"
    | "darkOnLight"
    | "darken"
    | "floating";
};

const baseStyle: Interpolation = {
  alignItems: "center",
  borderRadius: radius.xs,
  boxSizing: "border-box",
  fontFeatureSettings: "tnum",
  fontVariantNumeric: "tabular-nums",
  height: spacing.l,
  maxWidth: spacing.full,
  paddingBottom: spacing.xxs,
  paddingLeft: spacing.s,
  paddingRight: spacing.s,
  paddingTop: spacing.xxs,
  width: "fit-content",
};

const stylesVariant: Record<TagProps["variant"], Interpolation<ThemeType>> = {
  secondary: (theme) => ({
    backgroundColor: theme.semanticColors.secondary,
    color: theme.semanticColors.primary,
  }),
  accent: (theme) => ({
    backgroundColor: theme.semanticColors.accentLowest,
    color: theme.semanticColors.accentMedHigh,
  }),
  neutral: (theme) => ({
    backgroundColor: theme.semanticColors.neutralsLowest,
    color: theme.semanticColors.primary,
  }),
  lighten: {
    backgroundColor: constantSemanticColors.fixedLightenLow,
    color: constantSemanticColors.onColorHighest,
  },
  outline: {
    backgroundColor: colors.transparent,
    borderColor: constantSemanticColors.onColorHigh,
    borderStyle: "solid",
    borderWidth: stroke.light,
    color: constantSemanticColors.onColorHighest,
  },
  darkOnLight: {
    backgroundColor: constantSemanticColors.onColorHigh,
    color: constantSemanticColors.onColorLowest,
  },
  darken: {
    backgroundColor: constantSemanticColors.onColorLow,
    color: constantSemanticColors.onColorHighest,
  },
  floating: {
    backgroundColor: colors.transparent,
    color: constantSemanticColors.onColorHighest,
  },
};

export const Tag = ({
  asChild = false,
  children,
  variant = "secondary",
  textProps = {},
  className,
  ...props
}: TagProps) => {
  const DeterminedElement = determineElementFromAsChild({
    asChild,
    hallowElement: Element,
  });

  return (
    <Text
      css={[baseStyle, stylesVariant[variant]]}
      className={className}
      {...({
        asChild: true,
        overflow: "ellipsis",
        size: "l",
        type: "detail",
        ...textProps,
      } as TextProps)}
    >
      <DeterminedElement {...props}>{children}</DeterminedElement>
    </Text>
  );
};
